import { createMiddleware } from "@tanstack/react-start";
import { getRequest } from "@tanstack/react-start/server";
import { createClient } from "@/lib/supabase/admin";
import { resolveQrLinkHostMerchant } from "@/lib/stable-links";
import { SalesChannels, type SalesChannel } from "@/lib/storefront/channel";
import { hasStorefrontModule } from "@/lib/storefront/module-access";

export const supabaseUserMiddleware = createMiddleware({
  type: "function",
}).server(async ({ next }) => {
  const request = getRequest();
  // Try multiple sources to reliably determine the host
  let origin = request.headers.get("X-Forwarded-Host");

  if (!origin) {
    origin = request.headers.get("Host") ?? (undefined as unknown as string);
  }

  if (!origin) {
    try {
      // As a last resort, parse from request.url if available
      const url = new URL(request.url);
      origin = url.host;
    } catch {}
  }

  if (!origin) {
    throw new Error("Host not found in request");
  }

  if (origin.includes("vrodex-orders-") || origin.includes("orders-api")) {
    throw new Error("Orders origin not allowed");
  }

  // The stable QR host belongs to no merchant: a guest there is ordering from a
  // merchant without a domain, named by the code they scanned.
  const qrLinkHost = await resolveQrLinkHostMerchant(request);
  if (qrLinkHost) {
    if (!qrLinkHost.userId) {
      throw new Error("No merchant for this visitor on the QR link host");
    }

    return await next({
      context: {
        userId: qrLinkHost.userId,
        origin,
        isQrLinkHost: true,
        channel: SalesChannels.ordering as SalesChannel,
        onlineOrderConfigurationId: null as string | null,
      },
    });
  }

  const supabase = createClient();

  const {
    data: onlineOrderConfiguration,
    error: onlineOrderConfigurationError,
  } = await supabase
    .from("online_order_configuration")
    .select("id, type, user(id)")
    .eq("domain", origin)
    // The ordering site is the merchant's default configuration; a storefront
    // (webshop) is a configuration of its own on its own domain and is never
    // the default. Should both claim a domain, the ordering site keeps it.
    .or(`is_default.eq.true,type.eq.${SalesChannels.storefront}`)
    .order("is_default", { ascending: false })
    .limit(1)
    .maybeSingle();

  if (onlineOrderConfigurationError) {
    console.log(`ORIGIN: ${origin}`);
    console.error(onlineOrderConfigurationError);
    // Throw an error instead of redirect for server functions used in queries
    // Redirects can't be serialized and cause dehydration errors
    throw new Error("Could not fetch online order configuration");
  }

  if (!onlineOrderConfiguration) {
    console.error("User not found", origin);
    // Throw an error instead of redirect for server functions used in queries
    // Redirects can't be serialized and cause dehydration errors
    throw new Error(`User not found for domain: ${origin}`);
  }

  const isStorefront =
    onlineOrderConfiguration.type === SalesChannels.storefront;

  // The webshop closes with its module: the configuration and domain stay in
  // place, so switching the module back on reopens it as it was.
  if (
    isStorefront &&
    !(await hasStorefrontModule(onlineOrderConfiguration.user.id))
  ) {
    throw new Error(`Storefront not available for domain: ${origin}`);
  }

  return await next({
    context: {
      userId: onlineOrderConfiguration.user.id,
      origin,
      isQrLinkHost: false,
      channel: (isStorefront
        ? SalesChannels.storefront
        : SalesChannels.ordering) as SalesChannel,
      onlineOrderConfigurationId: onlineOrderConfiguration.id as
        | string
        | null,
    },
  });
});
